import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { AuctionGrid } from "@/components/auction-grid";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles } from "lucide-react";
import { StripeConnectCard } from "@/components/stripe-connect-card";
import { CardGridSkeleton, ErrorState } from "@/components/data-state";
import { PromoteListingDialog } from "@/components/promote-listing-dialog";
import { useState } from "react";
import { formatGBP } from "@/lib/format";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { useTranslation } from "@/i18n";

const COLS =
  "id,title,images,end_time,current_bid_pence,starting_price_pence,bid_count,grading_company,grade,status,promoted_until,auto_relist_enabled,relist_count";

export const Route = createFileRoute("/_authenticated/my-auctions")({
  head: () => ({ meta: [{ title: "My Auctions" }] }),
  component: MyAuctionsPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type Row = {
  id: string;
  title: string;
  images: string[] | null;
  end_time: string;
  current_bid_pence: number | null;
  starting_price_pence: number;
  bid_count: number;
  grading_company: string;
  grade: string | null;
  status: string;
  promoted_until: string | null;
  auto_relist_enabled: boolean;
  relist_count: number;
};

function ActiveRow({ a }: { a: Row }) {
  const { t } = useTranslation("activity");
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const [relist, setRelist] = useState(a.auto_relist_enabled);
  const toggleRelist = async () => {
    const next = !relist;
    setRelist(next);
    const { error } = await supabase.from("auctions").update({ auto_relist_enabled: next }).eq("id", a.id);
    if (error) { setRelist(!next); toast.error(error.message); return; }
    toast.success(next ? "Auto Relist turned on" : "Auto Relist turned off");
    qc.invalidateQueries({ queryKey: ["my-auctions"] });
  };
  const isPromoted = !!a.promoted_until && new Date(a.promoted_until) > new Date();
  const price = a.current_bid_pence ?? a.starting_price_pence;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-3">
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-surface-2">
        {a.images?.[0] && (
          <img src={a.images[0]} alt={a.title} className="h-full w-full object-cover" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <Link to="/auction/$slug" params={{ slug: buildAuctionSlug(a) }} className="line-clamp-1 font-medium hover:text-gold">
          {a.title}
        </Link>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>{formatGBP(price)}</span>
          <span>{t("myAuctions.bidsCount", "{count} bids", { count: a.bid_count })}</span>
          <span>{t("myAuctions.endsOn", "Ends {date}", { date: new Date(a.end_time).toLocaleString("en-GB") })}</span>
          <button type="button" onClick={toggleRelist} className="hover:text-foreground underline-offset-2 hover:underline">
            Auto Relist: {relist ? "ON" : "OFF"}
          </button>
          {a.relist_count > 0 && <span>Automatically Relisted</span>}
          {isPromoted && (
            <span className="inline-flex items-center gap-1 text-gold">
              <Sparkles className="h-3 w-3" /> {t("myAuctions.featuredUntil", "Featured until {date}", { date: new Date(a.promoted_until!).toLocaleString("en-GB") })}
            </span>
          )}
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-3 w-3 mr-1" /> {isPromoted ? t("myAuctions.extend", "Extend") : t("myAuctions.promote", "Promote")}
      </Button>
      <PromoteListingDialog
        open={open}
        onOpenChange={setOpen}
        auctionId={a.id}
        auctionStatus={a.status}
        auctionEndTime={a.end_time}
      />
    </div>
  );
}

function MyAuctionsPage() {
  const { t } = useTranslation("activity");
  const { user } = useAuth();
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["my-auctions", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auctions")
        .select(COLS)
        .eq("seller_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Row[];
    },
  });

  const active = (data ?? []).filter((a) => a.status === "active");
  const ended = (data ?? []).filter((a) => a.status !== "active");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-12">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{t("myAuctions.eyebrow", "Seller")}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl"><span className="text-gold-gradient">{t("myAuctions.title", "My Auctions")}</span></h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">{t("myAuctions.subtitle", "Manage your live listings and review past sales.")}</p>
        </div>
        <Button asChild className="bg-gold-gradient hover:opacity-90 shadow-[0_10px_30px_-10px_rgba(201,162,75,0.5)]">
          <Link to="/sell"><Plus className="h-4 w-4 mr-2" />{t("myAuctions.listACard", "List a card")}</Link>
        </Button>
      </header>
      <div className="mb-8">
        <StripeConnectCard />
      </div>
      {isLoading ? (
        <CardGridSkeleton count={8} />
      ) : isError ? (
        <ErrorState
          description={t("myAuctions.errorDescription", "We couldn't load your listings.")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : (
        <div className="space-y-10">
          <section>
            <h2 className="font-display text-2xl mb-4">{t("myAuctions.activeHeading", "Active ({count})", { count: active.length })}</h2>
            {active.length === 0 ? (
              <p className="text-muted-foreground">{t("myAuctions.noActiveListings", "No active listings. Create one to get started.")}</p>
            ) : (
              <div className="space-y-2">
                {active.map((a) => <ActiveRow key={a.id} a={a} />)}
              </div>
            )}
          </section>
          <section>
            <h2 className="font-display text-2xl mb-4">{t("myAuctions.endedHeading", "Ended ({count})", { count: ended.length })}</h2>
            <AuctionGrid auctions={ended as unknown as never[]} emptyText={t("myAuctions.nothingEndedYet", "Nothing ended yet.")} />
          </section>
        </div>
      )}
    </div>
  );
}
